import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { ChartContainer, ChartTooltip } from "@/components/ui/chart";
import type { MonthlyCollectsByOrigin } from "@/integrations/agreements/client";
import { formatMonthLabel, formatMonthLong } from "./collectsByOriginFormat";
import { COLLECTS_BY_ORIGIN_SERIES } from "./collectsByOriginSeries";

interface CollectsByOriginChartProps {
  entries: MonthlyCollectsByOrigin[];
}

/** Cor de eixo do Figma: black/40%, não um cinza fixo. Mesma do EvolutionChart. */
const AXIS_TICK_COLOR = "rgba(0,0,0,0.4)";
/** Rótulo do mês — `#737373` no Figma (node 345:16164). */
const MONTH_LABEL_COLOR = "#737373";

/**
 * Barras agrupadas de coletas recorrentes x sob demanda (Figma node 345:16164).
 *
 * Os quatro meses vêm garantidos pelo backend, inclusive os zerados — por isso
 * **não** existe filtro por valor aqui. Sumir com a coluna de um mês sem coleta
 * entortaria a comparação mês a mês, que é a razão do gráfico existir.
 */
export const CollectsByOriginChart = ({ entries }: CollectsByOriginChartProps) => (
  <ChartContainer config={{}} className="h-full w-full">
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={entries}
        barGap={4}
        barCategoryGap="25%"
        margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
      >
        <XAxis
          dataKey="month"
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fill: MONTH_LABEL_COLOR, fontWeight: 600 }}
          tickMargin={12}
          tickFormatter={(value) => formatMonthLabel(String(value))}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fill: AXIS_TICK_COLOR }}
          // 32px cortava o primeiro dígito de valores na casa dos milhares
          // ("4.000" vira "000") — a largura reservada é o texto inteiro do
          // tick, ancorado à direita.
          width={44}
          // Contagem de coletas: meia coleta não existe.
          allowDecimals={false}
        />

        {COLLECTS_BY_ORIGIN_SERIES.map((series) => (
          <Bar
            key={series.key}
            dataKey={series.key}
            name={series.label}
            fill={series.color}
            barSize={28}
            // Os quatro cantos, não só o topo — a base das barras é
            // arredondada no Figma.
            radius={8}
          />
        ))}

        <ChartTooltip
          cursor={false}
          content={({ active, payload, label }) => {
            if (!active || !payload?.length) return null;

            return (
              <div className="max-w-xs rounded-lg border-0 bg-white p-3 shadow-lg">
                <p className="mb-2 text-sm font-semibold">
                  {formatMonthLong(String(label))}
                </p>
                {payload.map((entry) => (
                  <div key={String(entry.dataKey)} className="mb-1 flex items-center gap-2">
                    <div
                      className="size-2 rounded-full"
                      style={{ backgroundColor: entry.color }}
                    />
                    <span className="text-xs">
                      {entry.name}: {Number(entry.value ?? 0).toLocaleString("pt-BR")}
                    </span>
                  </div>
                ))}
              </div>
            );
          }}
        />
      </BarChart>
    </ResponsiveContainer>
  </ChartContainer>
);
